import RangeSlider from "@visiblethread/react-components/dist/components/RangeSlider";
import IconWithTooltip from "components/IconWithTooltip";
import Checkbox, {
  checkboxStates,
} from "@visiblethread/react-components/dist/components/Checkbox";
import Button, {
  buttonVariants,
} from "@visiblethread/react-components/dist/components/Button";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as InfoIcon } from "icons/info-grey.svg";
import { editThreshold } from "slices/settings/actions/thresholds";
import { showModal } from "slices/app/actions/modals";
import { modalTypes } from "constants/types";
import { selectThresholds } from "slices/settings/selectors";

const ThresholdContent = () => {
  const dispatch = useDispatch();
  // eslint-disable-next-line no-undef
  const { roles } = UserInfo;
  const hasPermissions = roles.split(",").includes("readability_settings");
  const thresholds = useSelector(selectThresholds);

  const [longLeft, setLongLeft] = useState(0);
  const [longRight, setLongRight] = useState(0);
  const [passiveLeft, setPassiveLeft] = useState(0);
  const [passiveRight, setPassiveRight] = useState(0);
  const [avgLeft, setAvgLeft] = useState(0);
  const [avgRight, setAvgRight] = useState(0);
  const [fleschLeft, setFleschLeft] = useState(0);
  const [fleschRight, setFleschRight] = useState(0);
  const [lixLeft, setLixLeft] = useState(0);
  const [lixRight, setLixRight] = useState(0);
  const [gradeLeft, setGradeLeft] = useState(0);
  const [gradeRight, setGradeRight] = useState(0);

  const [longSentence, setLongSentence] = useState(0);
  const [veryLongSentence, setVeryLongSentence] = useState(0);
  const [lixEnabled, setLixEnabled] = useState(false);

  const [changesMade, setChangesMade] = useState(false);

  useEffect(() => {
    setChangesMade(false);

    setLongLeft(100 - thresholds.thresholdLongSentenceUpper);
    setLongRight(100 - thresholds.thresholdLongSentenceLower);
    setPassiveLeft(100 - thresholds.thresholdPassiveVoiceUpper);
    setPassiveRight(100 - thresholds.thresholdPassiveVoiceLower);
    setAvgLeft(100 - thresholds.thresholdAvgSentenceUpper);
    setAvgRight(100 - thresholds.thresholdAvgSentenceLower);
    setFleschLeft(thresholds.thresholdReadabilityLower);
    setFleschRight(thresholds.thresholdReadabilityUpper);
    setLixLeft(100 - thresholds.thresholdLixReadabilityUpper);
    setLixRight(100 - thresholds.thresholdLixReadabilityLower);
    setGradeLeft(20 - thresholds.thresholdGradeLevelUpper);
    setGradeRight(20 - thresholds.thresholdGradeLevelLower);

    setLongSentence(thresholds.longSentenceCutoff);
    setVeryLongSentence(thresholds.veryLongSentenceCutoff);
    setLixEnabled(thresholds.lixAlwaysEnabled);
  }, [thresholds]);

  useEffect(() => {
    setChangesMade(true);
  }, [
    longLeft,
    longRight,
    passiveLeft,
    passiveRight,
    avgLeft,
    avgRight,
    fleschLeft,
    fleschRight,
    lixLeft,
    lixRight,
    gradeLeft,
    gradeRight,
    longSentence,
    veryLongSentence,
    lixEnabled,
  ]);

  const handleReset = () => {
    dispatch(showModal(modalTypes.RESET_DEFAULTS_THRESHOLDS));
  };

  const handleSubmit = () => {
    dispatch(
      editThreshold({
        thresholdLongSentenceLower: 100 - longRight,
        thresholdLongSentenceUpper: 100 - longLeft,
        thresholdPassiveVoiceLower: 100 - passiveRight,
        thresholdPassiveVoiceUpper: 100 - passiveLeft,
        thresholdAvgSentenceLower: 100 - avgRight,
        thresholdAvgSentenceUpper: 100 - avgLeft,
        thresholdReadabilityLower: fleschLeft,
        thresholdReadabilityUpper: fleschRight,
        thresholdLixReadabilityLower: 100 - lixRight,
        thresholdLixReadabilityUpper: 100 - lixLeft,
        thresholdGradeLevelLower: 20 - gradeRight,
        thresholdGradeLevelUpper: 20 - gradeLeft,
        longSentenceCutoff: longSentence,
        veryLongSentenceCutoff: veryLongSentence,
        lixAlwaysEnabled: lixEnabled,
      })
    );
  };

  const longSentencesSlider = () => (
    <div className="readability_thresholds__sliders__row">
      <span>Long Sentences:</span>
      <span>{100 - longLeft}%</span>
      <RangeSlider
        max={100}
        leftHandle={longLeft}
        setLeftHandle={setLongLeft}
        rightHandle={longRight}
        setRightHandle={setLongRight}
      />
      <span>{100 - longRight}%</span>
    </div>
  );

  const passiveVoiceSlider = () => (
    <div className="readability_thresholds__sliders__row">
      <span>Passive Voice:</span>
      <span>{100 - passiveLeft}%</span>
      <RangeSlider
        max={100}
        leftHandle={passiveLeft}
        setLeftHandle={setPassiveLeft}
        rightHandle={passiveRight}
        setRightHandle={setPassiveRight}
      />
      <span>{100 - passiveRight}%</span>
    </div>
  );

  const avgSentenceSlider = () => (
    <div className="readability_thresholds__sliders__row">
      <span>Average Sentence Length:</span>
      <span>{100 - avgLeft}%</span>
      <RangeSlider
        max={100}
        leftHandle={avgLeft}
        setLeftHandle={setAvgLeft}
        rightHandle={avgRight}
        setRightHandle={setAvgRight}
      />
      <span>{100 - avgRight}%</span>
    </div>
  );

  const fleschSlider = () => (
    <div className="readability_thresholds__sliders__row">
      <span>
        Readability (Flesch):
        <IconWithTooltip
          icon={<InfoIcon width={11} height={11} />}
          tooltipText="Readability Flesch Index is used to rate the complexity of English content.<br><br>This index is based on the Flesch Reading Ease and ranges from 0 to 100, 100 being the best mark."
        />
      </span>
      <span>{fleschLeft}</span>
      <RangeSlider
        max={100}
        leftHandle={fleschLeft}
        setLeftHandle={setFleschLeft}
        rightHandle={fleschRight}
        setRightHandle={setFleschRight}
      />
      <span>{fleschRight}</span>
    </div>
  );

  const lixSlider = () => (
    <div className="readability_thresholds__sliders__row">
      <span title="Readability Lix index is used to rate the complexity of content, in particular non-English content where the Flesch index does not apply. Scores range from 100  to 0, with 0 being the best.">
        Readability (LIX)
        <IconWithTooltip
          icon={<InfoIcon width={11} height={11} />}
          tooltipText="Readability Lix index is used to rate the complexity of content, in particular non-English content where the Flesch index does not apply.<br><br>Scores range from 100  to 0, with 0 being the best."
        />
      </span>
      <span>{100 - lixLeft}</span>
      <RangeSlider
        max={100}
        leftHandle={lixLeft}
        setLeftHandle={setLixLeft}
        rightHandle={lixRight}
        setRightHandle={setLixRight}
      />
      <span>{100 - lixRight}</span>
    </div>
  );

  const gradeLevelSlider = () => (
    <div className="readability_thresholds__sliders__row">
      <span>Grade Level:</span>
      <span>{20 - gradeLeft}</span>
      <RangeSlider
        max={20}
        leftHandle={gradeLeft}
        setLeftHandle={setGradeLeft}
        rightHandle={gradeRight}
        setRightHandle={setGradeRight}
      />
      <span>{20 - gradeRight}</span>
    </div>
  );

  const longSentencesLimit = () => (
    <>
      <h2>Long Sentence Limits</h2>
      <p>
        Treat sentences with
        <input
          type="text"
          value={longSentence}
          onChange={(e) => {
            setLongSentence(Number(e.target.value));
          }}
          disabled={!hasPermissions}
        />
        words or greater as <strong>Long</strong>
      </p>
      <p>
        Treat sentences with
        <input
          type="text"
          value={veryLongSentence}
          onChange={(e) => setVeryLongSentence(Number(e.target.value))}
          disabled={!hasPermissions}
        />
        words or greater as <strong>Very Long</strong>
      </p>
    </>
  );

  const lixSettings = () => (
    <>
      <h2>LIX Settings</h2>
      <div className="readability_thresholds__lix_settings">
        <span>
          Show LIX scores for folders which contain only English language files
        </span>
        <IconWithTooltip
          icon={<InfoIcon width={11} height={11} />}
          tooltipText="LIX is a readability score normally used for non-English content.<br><br>For example French, Spanish and German docs. However, it also works well for English content. <br><br>Check this box if you want to show the LIX score for all folders."
        />
        <Checkbox
          state={lixEnabled ? checkboxStates.checked : checkboxStates.empty}
          handleChange={() => {
            setLixEnabled(!lixEnabled);
          }}
          disabled={!hasPermissions}
        />
      </div>
    </>
  );

  const buttons = () => (
    <div className="readability_thresholds__buttons">
      <Button
        onClick={handleReset}
        variant={buttonVariants.secondary}
        content="Reset Defaults"
        disabled={!hasPermissions}
      />
      <Button
        onClick={handleSubmit}
        variant={buttonVariants.primary}
        content="Save Changes"
        disabled={!changesMade || !hasPermissions}
      />
    </div>
  );

  return (
    <div
      className="readability_thresholds__content"
      data-testid="thresholdsContent"
    >
      <div className="readability_thresholds__sliders">
        <div className="readability_thresholds__sliders__header">
          <span>Bad</span> <span>Good</span>
        </div>
        {longSentencesSlider()}
        {passiveVoiceSlider()}
        {avgSentenceSlider()}
        {fleschSlider()}
        {lixSlider()}
        {gradeLevelSlider()}
      </div>

      <hr />
      {longSentencesLimit()}

      <hr />
      {lixSettings()}

      <hr />
      {buttons()}
    </div>
  );
};

export default ThresholdContent;
